import { motion } from "framer-motion";
import { Database, ShieldCheck, CheckCircle2, Cpu, Filter } from "lucide-react";

/* 1. Interactive Pipeline & Data Lake Dashboard (For Intro) */
export function DataLakeVisual() {
  return (
    <div className="about-visual about-visual--datalake">
      <div className="about-visual__glass-card">
        {/* Card Header Bar */}
        <div className="about-visual__header">
          <div className="about-visual__title-group">
            <div className="about-visual__icon-hub">
              <Database size={18} />
            </div>
            <div>
              <h4 className="about-visual__title">Enterprise Data Stream</h4>
              <p className="about-visual__subtitle">SMTP Active &bull; Multi-Tenant Validation</p>
            </div>
          </div>
          <span className="about-visual__badge-live">
            <span className="about-visual__dot-pulse" /> LIVE SCRUB
          </span>
        </div>

        {/* Dynamic Metric Nodes */}
        <div className="about-visual__stat-chips">
          <div className="about-visual__chip">
            <span className="about-visual__chip-lbl">Clean Delivery</span>
            <strong className="about-visual__chip-val text-emerald">98%</strong>
          </div>
          <div className="about-visual__chip">
            <span className="about-visual__chip-lbl">Bounce Cap</span>
            <strong className="about-visual__chip-val text-cyan">&lt; 1.8%</strong>
          </div>
          <div className="about-visual__chip">
            <span className="about-visual__chip-lbl">Latency</span>
            <strong className="about-visual__chip-val text-indigo">12ms</strong>
          </div>
        </div>

        {/* Live Processing Row Items */}
        <div className="about-visual__feed">
          {[
            { tag: "CXO Profile", val: "Verified VP of Growth", color: "#38bdf8" },
            { tag: "Domain Auth", val: "SPF / DKIM / DMARC Handshake", color: "#10b981" },
            { tag: "Scrubbed", val: "Spam Traps & Honey Pots Wiped", color: "#6366f1" },
          ].map((item, i) => (
            <motion.div
              key={i}
              className="about-visual__feed-item"
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.12 * i, duration: 0.35 }}
            >
              <CheckCircle2 size={15} style={{ color: item.color, flexShrink: 0 }} />
              <span className="about-visual__feed-text">{item.val}</span>
              <span className="about-visual__feed-tag">{item.tag}</span>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Floating Orbital Badge */}
      <motion.div 
        className="about-visual__float-badge"
        animate={{ y: [0, -5, 0] }}
        transition={{ repeat: Infinity, duration: 3.5, ease: "easeInOut" }}
      >
        <ShieldCheck size={18} className="text-emerald" />
        <div className="about-visual__float-badge-text">
          <strong>GDPR & CCPA Compliant</strong>
          <span>Strict Privacy Protocols</span>
        </div>
      </motion.div>
    </div>
  );
}

/* 2. Pipeline Ingestion Stage Engine (For 5 Steps) */
export function PipelineVisual() {
  return (
    <div className="about-visual about-visual--pipeline">
      <div className="about-visual__glass-card">
        <div className="about-visual__header">
          <div className="about-visual__title-group">
            <div className="about-visual__icon-hub about-visual__icon-hub--cyan">
              <Cpu size={18} />
            </div>
            <div>
              <h4 className="about-visual__title">Targeted Ingestion Hub</h4>
              <p className="about-visual__subtitle">5-Stage Continuous Filtering</p>
            </div>
          </div>
          <Filter size={16} className="text-muted" />
        </div>

        <div className="about-visual__progress-rows">
          {[
            { step: "01", name: "ICP Demographic Parsing", status: "Completed", pct: "100%" },
            { step: "02", name: "Decision-Maker Matrix", status: "Active Filtering", pct: "85%" },
            { step: "03", name: "Geolocation Matching", status: "Syncing", pct: "64%" },
          ].map((row, idx) => (
            <div key={idx} className="about-visual__row-box">
              <div className="about-visual__row-meta">
                <span className="about-visual__row-name">
                  <span className="about-visual__step-num">{row.step}</span> {row.name}
                </span>
                <span className="about-visual__row-status">{row.status}</span>
              </div>
              <div className="about-visual__track">
                <div className="about-visual__bar" style={{ width: row.pct }} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* 3. Radial Accuracy Core (For Skill Section) */
export function QualityRingVisual() {
  return (
    <div className="about-visual about-visual--radial">
      <div className="about-visual__glass-card text-center">
        <div className="about-visual__ring-container">
          <svg className="about-visual__svg-ring" viewBox="0 0 120 120">
            <circle cx="60" cy="60" r="50" className="about-visual__ring-bg" strokeWidth="8" fill="none" />
            <motion.circle
              cx="60"
              cy="60"
              r="50"
              className="about-visual__ring-accent"
              strokeWidth="8"
              strokeDasharray="314"
              strokeDashoffset="314"
              strokeLinecap="round"
              fill="none"
              animate={{ strokeDashoffset: 314 * (1 - 0.99) }}
              transition={{ duration: 1.6, ease: "easeOut" }}
            />
          </svg>
          <div className="about-visual__ring-label">
            <span className="about-visual__ring-value">85%</span>
            <span className="about-visual__ring-sub">Accuracy</span>
          </div>
        </div>
        <h4 className="about-visual__title">Guaranteed Clean Leads</h4>
        <p className="about-visual__subtitle mt-1">
          Multi-layer verification ensures inbox deliverability and zero dead MX records.
        </p>
      </div>
    </div>
  );
}